<template>
    <div class="bg-grey-100 py-4">
        <AppContainer>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                    <AppButton theme="white" :href="route('advice.index')">
                        Back to Tyre Help & Advice
                    </AppButton>

                    <h1 class="text-5xl text-dark mt-16 tracking-wide font-bold">{{
                            seoTags?.h1_tag ?? article.title
                        }}</h1>

                    <p class="mt-4 text-dark">{{ postDate }}</p>
                </div>

                <div class="order-first md:order-last">
                    <!-- If we have no image, but a featured video, we can use that in place -->
                    <template v-if="!article.image && featuredVideo">
                        <AppVideo
                            :type="featuredVideo.type"
                            :clip="featuredVideo.video_id"></AppVideo>

                        <SubscribeButton class="mt-2 text-right"></SubscribeButton>
                    </template>

                    <img v-else :src="article.image ?? '/images/misc/default_news.jpg'"
                         class="object-cover w-full aspect-video rounded-lg">
                </div>
            </div>
        </AppContainer>
    </div>

    <AppContainer>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 my-2">
            <div>
                <template v-if="article.excerpt">
                    <p>{{ article.excerpt }}</p>

                    <div class="my-8 w-24 bg-primary h-1"></div>
                </template>

                <div class="mb-4" v-html="article.description"></div>

                <template v-if="article.cta_text && article.cta_link">
                    <AppButton v-if="!ctaIsExternal" :href="article.cta_link">
                        {{ article.cta_text }}
                    </AppButton>

                    <a v-else :href="article.cta_link" target="_blank">
                        <AppButton>
                            {{ article.cta_text }}
                        </AppButton>
                    </a>
                </template>
            </div>

            <div>
                <div>
                    <CollapseHeight>
                        <div v-if="products.length > 0" class="mb-16">
                            <h2 class="text-2xl text-dark font-bold mb-4">Featured reviews</h2>

                            <div class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                <TyreCard v-for="product of products" :product="product"></TyreCard>
                            </div>
                        </div>
                    </CollapseHeight>

                    <template v-if="noneFeaturedVideos.length > 0">
                        <h2 class="text-2xl text-dark font-bold my-4 mt-16">Watch now</h2>

                        <div class="grid grid-cols-1 gap-4">
                            <AppVideo v-for="video of noneFeaturedVideos"
                                      :clip="video.video_id"
                                      :type="video.type"></AppVideo>
                        </div>
                    </template>

                    <div v-if="moreArticles.length > 0">
                        <h2 class="text-2xl text-dark font-bold my-4">
                            Read more
                        </h2>

                        <div class="flex flex-col gap-4">
                            <div v-for="extraArticle of moreArticles" class="grid grid-cols-3 items-center gap-4">
                                <img v-if="extraArticle.image || extraArticle.videos.length === 0"
                                     :src="extraArticle.image ?? '/images/misc/default_news.jpg'"
                                     class="object-cover aspect-video rounded-lg"
                                     height="107">

                                <AppVideo v-else thumbnail-only
                                          :type="extraArticle.videos[0].type"
                                          :clip="extraArticle.videos[0].video_id"></AppVideo>

                                <div class="col-span-2">
                                    <p class="text-lg font-bold pr-4">{{ extraArticle.title }}</p>

                                    <AppButton class="mt-4" :href="route('advice.show', extraArticle)">
                                        Read now
                                    </AppButton>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="my-4">
                        <component is="script" async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-8286486938913131"
                                   crossorigin="anonymous"></component>
                        <!-- News Ads -->
                        <component is="ins" class="adsbygoogle"
                                   style="display:block"
                                   data-ad-client="ca-pub-8286486938913131"
                                   data-ad-slot="1372330416"
                                   data-ad-format="auto"
                                   data-full-width-responsive="true"></component>
                        <component is="script">
                            (adsbygoogle = window.adsbygoogle || []).push({});
                        </component>
                    </div>

                    <h2 class="text-2xl text-dark font-bold my-4 mt-16">
                        Follow us
                    </h2>

                    <div class="flex items-center gap-4">
                        <a href="https://www.youtube.com/channel/UCKCkxxpQ7-Wdn_OOyxgfvwA"
                           target="_blank"
                           class="rounded-full w-12 h-12 flex items-center justify-center bg-dark group">
                            <FontAwesomeIcon size="lg"
                                             class="text-white group-hover:scale-110 group-hover:rotate-12 transition"
                                             :icon="['fab', 'youtube']"></FontAwesomeIcon>
                        </a>
                        <a href="https://www.instagram.com/tyreseeker/"
                           target="_blank"
                           class="rounded-full w-12 h-12 flex items-center justify-center bg-dark group">
                            <FontAwesomeIcon size="lg"
                                             class="text-white group-hover:scale-110 group-hover:-rotate-12 transition"
                                             :icon="['fab', 'instagram']"></FontAwesomeIcon>
                        </a>
                        <a href="https://www.facebook.com/tyreseekeruk/"
                           target="_blank"
                           class="rounded-full w-12 h-12 flex items-center justify-center bg-dark group">
                            <FontAwesomeIcon size="lg"
                                             class="text-white group-hover:scale-110 group-hover:rotate-12 transition"
                                             :icon="['fab', 'facebook-f']"></FontAwesomeIcon>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </AppContainer>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import AppContainer from "@/Layouts/AppContainer";
import AppButton from "@/Components/AppButton";
import TyreCard from "@/Components/Products/Tyres/TyreCard";
import AppVideo from "@/Components/AppVideo";
import AppHead from "@/Components/AppSeoTags";
import CollapseHeight from "@/Components/Transitions/CollapseHeight";
import SubscribeButton from "@/Components/Videos/SubscribeButton";

dayjs.extend(advancedFormat);

const props = defineProps({
    article: Object,
    videos: Array,
    seoTags: Object,
    moreArticles: Array
});

const postDate = computed(() => dayjs(props.article.created_at).format("Do MMMM YYYY"));

const featuredVideo = computed(() => {
    return props.videos.find(video => video.featured);
});

const noneFeaturedVideos = computed(() => props.videos.filter(video => !video.featured));

const products = ref([]);

// Check if the CTA link is to an external site
const ctaIsExternal = computed(() => {
    if (!props.article.cta_link) return false;

    const match = props.article.cta_link.match(/^([^:\/?#]+:)?(?:\/\/([^\/?#]*))?([^?#]+)?(\?[^#]*)?(#.*)?/);

    if (typeof match[1] === "string" && match[1].length > 0 && match[1].toLowerCase() !== location.protocol) return true;

    return typeof match[2] === "string" && match[2].length > 0 && match[2].replace(new RegExp(":(" + {
        "http:": 80,
        "https:": 443
    }[location.protocol] + ")?$"), "") !== location.host;
});

async function getAdvicePageProducts() {
    const response = await axios.get(route("api.advice.products.index", props.article));

    products.value = response.data;
}

onMounted(() => getAdvicePageProducts());
</script>

<style scoped>

</style>
